import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
} from "antd";
import { ClearOutlined, EyeOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import SuperAdminContext from "../../context/SuperAdminContext";
import { CardComponent, ColumnComponent } from "../../components/ui";

interface FlushRow {
  key: string;
  label: string;
  count: number;
}

const labels: Record<string, string> = {
  invoices: "Invoices",
  journalEntries: "Journal Entries",
  deposits: "Deposits",
  payments: "Payments",
  creditMemos: "Credit Memos",
};

export const SuperAdminFlushPage = () => {
  const {
    companies,
    flushPreview,
    superAdminLoading,
    getCompanies,
    getFlushPreview,
    flushCompany,
  } = useContext(SuperAdminContext);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deletedCounts, setDeletedCounts] = useState<Record<string, number> | null>(
    null,
  );

  useEffect(() => {
    getCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCompany = companies.find(
    (company) => company._id === selectedCompanyId,
  );

  const rows = useMemo<FlushRow[]>(
    () =>
      flushPreview
        ? Object.entries(flushPreview.counts).map(([key, count]) => ({
            key,
            label: labels[key] || key,
            count,
          }))
        : [],
    [flushPreview],
  );

  const companyOptions = companies.map((company) => ({
    label: company.name,
    value: company._id,
  }));

  const columns: ColumnsType<FlushRow> = [
    { title: "Transaction Type", dataIndex: "label", key: "label" },
    {
      title: "Records Eligible for Deletion",
      dataIndex: "count",
      key: "count",
      align: "right",
      render: (value) => <strong>{value}</strong>,
    },
  ];

  const runFlush = () => {
    if (!selectedCompanyId || !selectedCompany) return;
    flushCompany(selectedCompanyId, confirmName, (counts) => {
      setDeletedCounts(counts);
      setConfirmOpen(false);
      setConfirmName("");
      getFlushPreview(selectedCompanyId);
    });
  };

  return (
    <div className="super-admin">
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <CardComponent
            className="summary-client__card"
            title="Database Flush"
            extra={
              <Space className="super-admin__filters" wrap>
                <Select
                  showSearch
                  placeholder="Select company"
                  value={selectedCompanyId}
                  options={companyOptions}
                  optionFilterProp="label"
                  onChange={(value) => {
                    setSelectedCompanyId(value);
                    setDeletedCounts(null);
                  }}
                  style={{ minWidth: 280 }}
                />
                <Button
                  icon={<EyeOutlined />}
                  disabled={!selectedCompanyId}
                  onClick={() => selectedCompanyId && getFlushPreview(selectedCompanyId)}
                >
                  Preview
                </Button>
                <Button
                  danger
                  type="primary"
                  icon={<ClearOutlined />}
                  disabled={!flushPreview || flushPreview.total === 0}
                  onClick={() => setConfirmOpen(true)}
                >
                  Flush
                </Button>
              </Space>
            }
          />
        </ColumnComponent>

        <ColumnComponent>
          <Alert
            showIcon
            type="warning"
            className="super-admin__alert"
            message="Flush permanently deletes inactive or zero-balance transaction records after sending a backup email to the super admin user."
          />
        </ColumnComponent>

        {flushPreview && (
          <>
            <ColumnComponent md={8} lg={6}>
              <div className="super-admin__stat">
                <Statistic title="Eligible Records" value={flushPreview.total} />
              </div>
            </ColumnComponent>
            <ColumnComponent md={16} lg={18}>
              <Table
                bordered
                size="small"
                rowKey="key"
                columns={columns}
                dataSource={rows}
                loading={superAdminLoading}
                pagination={false}
              />
            </ColumnComponent>
          </>
        )}

        {deletedCounts && (
          <ColumnComponent>
            <Alert
              showIcon
              type="success"
              message={`Flush completed for ${selectedCompany?.name}`}
              description={Object.entries(deletedCounts)
                .map(([key, count]) => `${labels[key] || key}: ${count}`)
                .join(" | ")}
            />
          </ColumnComponent>
        )}
      </Row>

      <Modal
        open={confirmOpen}
        title="Confirm database flush"
        onCancel={() => setConfirmOpen(false)}
        onOk={runFlush}
        okButtonProps={{
          danger: true,
          disabled: confirmName !== selectedCompany?.name,
          loading: superAdminLoading,
        }}
        okText="Send backup and flush"
      >
        <Space direction="vertical" className="super-admin__stack">
          <Typography.Text>
            Type <strong>{selectedCompany?.name}</strong> to confirm.
          </Typography.Text>
          <Input value={confirmName} onChange={(event) => setConfirmName(event.target.value)} />
        </Space>
      </Modal>
    </div>
  );
};
